/**
 * Gets the computed font size of the root HTML element.
 *
 * @returns The root element font size in pixels, defaults to 16 if document is unavailable
 */
function getRootElementFontSize(): number {
  if (typeof document === 'undefined') {
    return 16
  }
  // Returns a number of the computed font-size, so in px for the root <html> element
  return parseFloat(getComputedStyle(document.documentElement).fontSize)
}

/**
 * Converts a rem value to pixels based on the root element font size.
 *
 * @param value - The rem value to convert
 * @returns The equivalent value in pixels
 *
 * @example
 * ```typescript
 * convertRemToPx(1) // returns 16 (if root font size is 16px)
 * convertRemToPx(2.5) // returns 40 (if root font size is 16px)
 * ```
 */
export function convertRemToPx(value: number): number {
  return value * getRootElementFontSize()
}

/**
 * Calculates the optimal position for a tooltip relative to a given element.
 *
 * Determines whether the tooltip should appear on the left or right side,
 * and whether the arrow should point to the top or bottom of the element.
 *
 * @param node - The HTML element to position the tooltip relative to
 * @returns An object containing the optimal position and arrow placement
 *
 * @example
 * ```typescript
 * const button = document.getElementById('myButton')
 * const { position, arrow } = getTooltipPosition(button)
 * // { position: 'right', arrow: 'top' }
 * ```
 */
export function getTooltipPosition(node: HTMLElement): {
  position: 'left' | 'right'
  arrow: 'top' | 'bottom'
} {
  const { top, left } = node.getBoundingClientRect()
  const { innerWidth, innerHeight } = window
  const bottom = innerHeight - top
  const right = innerWidth - left
  if (right > left) {
    if (top > bottom) {
      return { position: 'right', arrow: 'bottom' }
    } else {
      return { position: 'right', arrow: 'top' }
    }
  } else {
    if (top > bottom) {
      return { position: 'left', arrow: 'bottom' }
    } else {
      return { position: 'left', arrow: 'top' }
    }
  }
}

/**
 * Calculates the optimal position for a tooltip with specific dimensions.
 *
 * Enhanced version that considers tooltip width and max height to provide
 * more precise positioning with center arrow option.
 *
 * @param node - The HTML element to position the tooltip relative to
 * @param tooltipWidth - The width of the tooltip in pixels
 * @param tooltipMaxHeight - The maximum height of the tooltip in pixels
 * @returns An object containing the optimal position and arrow placement
 *
 * @example
 * ```typescript
 * const element = document.querySelector('.target')
 * const { position, arrow } = getTooltipPosition2(element, 200, 150)
 * // { position: 'left', arrow: 'center' }
 * ```
 */
export function getTooltipPosition2(
  node: HTMLElement,
  tooltipWidth: number,
  tooltipMaxHeight: number,
): {
  position: 'left' | 'right'
  arrow: 'top' | 'center' | 'bottom'
} {
  const { top, left } = node.getBoundingClientRect()
  const { innerWidth, innerHeight } = window
  const bottom = innerHeight - top
  const right = innerWidth - left

  let position
  if (right > tooltipWidth) {
    position = 'right'
  } else {
    position = 'left'
  }

  let arrow
  if (tooltipMaxHeight / 2 > bottom) {
    arrow = 'bottom'
  } else if (tooltipMaxHeight / 2 > top) {
    arrow = 'top'
  } else {
    arrow = 'center'
  }

  return { position, arrow }
}

/**
 * Gets the horizontal scroll offset of the page.
 *
 * Cross-browser compatible function that works with both modern and legacy browsers.
 *
 * @returns The horizontal scroll offset in pixels
 *
 * @example
 * ```typescript
 * const scrollX = getPageXOffset() // returns current horizontal scroll position
 * ```
 */
export function getPageXOffset(): number {
  return (
    (typeof window !== 'undefined' && window.pageXOffset) ||
    (document.documentElement && document.documentElement.scrollLeft) ||
    0
  )
}

/**
 * Gets the vertical scroll offset of the page.
 *
 * Cross-browser compatible function that works with both modern and legacy browsers.
 *
 * @returns The vertical scroll offset in pixels
 *
 * @example
 * ```typescript
 * const scrollY = getPageYOffset() // returns current vertical scroll position
 * ```
 */
export function getPageYOffset(): number {
  return (
    (typeof window !== 'undefined' && window.pageYOffset) ||
    (document.documentElement && document.documentElement.scrollTop) ||
    0
  )
}

/**
 * Generates CSS styles for popover dropdown positioning.
 *
 * Calculates appropriate styles based on the container element and positioning type.
 * Supports both absolute and fixed positioning strategies.
 *
 * @param container - The HTML element that contains the popover
 * @param position - The CSS position type ('absolute' or 'fixed')
 * @returns Style object with position, top, and width properties, or undefined
 *
 * @example
 * ```typescript
 * const container = document.querySelector('.dropdown-container')
 * const styles = getPopoverDropdownStyle(container, 'fixed')
 * // { position: 'fixed', top: '45px', width: '200px' }
 * ```
 */
export const getPopoverDropdownStyle = (
  container: HTMLElement,
  position: 'absolute' | 'fixed',
):
  | undefined
  | {
      position: string
      top: string | number
      width: string
    } => {
  if (position === 'absolute') {
    return {
      position,
      top: 0,
      width: '100%',
    }
  }
  if (position === 'fixed') {
    const rect = container.getBoundingClientRect()
    const width = rect.width
    const y = rect.top + rect.height
    return {
      position,
      top: `${y}px`,
      width: `${width}px`,
    }
  }
}

/**
 * Calculates the number of text lines in a container element.
 *
 * Determines line count by dividing the container height by the line height.
 * Falls back to a default line height calculation if CSS line-height is not set.
 *
 * @param container - The HTML element containing the text
 * @returns The estimated number of text lines
 *
 * @example
 * ```typescript
 * const textDiv = document.querySelector('.text-content')
 * const lineCount = getTextLinesCount(textDiv) // returns 3
 * ```
 */
export const getTextLinesCount = (container: HTMLElement): number => {
  const height = Math.ceil(container.offsetHeight)

  const containerStyle = getComputedStyle(container)
  const defaultLineHeight = parseInt(containerStyle.fontSize, 10) * 1.2
  const lineHeight = parseInt(containerStyle.lineHeight, 10)
  return Math.ceil(
    height / (isNaN(lineHeight) ? defaultLineHeight : lineHeight),
  )
}
